import React, { useEffect, useMemo, useState } from "react";
import { FaCheck, FaRedo, FaTimes } from "react-icons/fa";

import {
  DEFAULT_TECHNIQUE_IMAGE,
  buildQuizQuestions,
} from "../utils/techniquesRevisionUtils";

function TechniqueQuizPanel({ techniques = [] }) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [errors, setErrors] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const canStartQuiz = techniques.length >= 3;

  const currentQuestion = questions[currentQuestionIndex];

  const progress = useMemo(() => {
    if (!questions.length) return 0;
    return Math.round(((currentQuestionIndex + 1) / questions.length) * 100);
  }, [currentQuestionIndex, questions.length]);

  const generateQuiz = () => {
    const nextQuestions = buildQuizQuestions(techniques, 10);

    setQuestions(nextQuestions);
    setCurrentQuestionIndex(0);
    setScore(0);
    setErrors([]);
    setShowResults(false);
  };

  useEffect(() => {
    if (canStartQuiz) {
      generateQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [techniques]);

  const handleAnswer = (answer) => {
    if (!currentQuestion) return;

    if (answer === currentQuestion.correctAnswer) {
      setScore((prev) => prev + 1);
    } else {
      setErrors((prev) => [
        ...prev,
        {
          ...currentQuestion,
          selectedAnswer: answer,
        },
      ]);
    }

    if (currentQuestionIndex >= questions.length - 1) {
      setShowResults(true);
      return;
    }

    setCurrentQuestionIndex((prev) => prev + 1);
  };

  if (!canStartQuiz) {
    return (
      <div className="mt-6 rounded-[2rem] border border-dashed border-slate-300 bg-white p-8 text-center shadow-xl shadow-slate-200/50 dark:border-white/10 dark:bg-white/[0.05]">
        <h2 className="font-titre text-2xl font-bold text-slate-950 dark:text-white">
          Pas assez de techniques pour lancer le quiz
        </h2>

        <p className="mx-auto mt-3 max-w-xl text-sm font-medium leading-7 text-slate-500 dark:text-white/50">
          Modifie les filtres pour afficher au moins 3 techniques.
        </p>
      </div>
    );
  }

  if (showResults) {
    return (
      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-sky-600 dark:text-sky-200">
              Résultat du quiz
            </p>

            <h2 className="mt-2 font-titre text-3xl font-bold text-slate-950 dark:text-white">
              Score : {score} / {questions.length}
            </h2>
          </div>

          <button
            type="button"
            onClick={generateQuiz}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5"
          >
            <FaRedo />
            Recommencer
          </button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {questions.map((question) => {
            const error = errors.find(
              (item) => item.id === question.id,
            );

            const isCorrect = !error;

            return (
              <article
                key={question.id}
                className={`overflow-hidden rounded-[1.5rem] border ${
                  isCorrect
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-400/20 dark:bg-emerald-500/10"
                    : "border-red-200 bg-red-50 dark:border-red-400/20 dark:bg-red-500/10"
                }`}
              >
                <img
                  src={question.image || DEFAULT_TECHNIQUE_IMAGE}
                  alt={question.correctAnswer}
                  className="h-44 w-full object-cover"
                />

                <div className="p-4">
                  <div
                    className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-2xl ${
                      isCorrect
                        ? "bg-emerald-600 text-white"
                        : "bg-red-600 text-white"
                    }`}
                  >
                    {isCorrect ? <FaCheck /> : <FaTimes />}
                  </div>

                  {error && (
                    <p className="text-sm font-bold text-red-700 line-through dark:text-red-200">
                      {error.selectedAnswer}
                    </p>
                  )}

                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    {question.correctAnswer}
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section className="mt-6 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
      <div className="bg-gradient-to-br from-slate-950 via-sky-950 to-blue-700 p-5 text-white sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">
              Quiz visuel
            </p>

            <h2 className="mt-2 font-titre text-3xl font-bold">
              Quelle est cette technique ?
            </h2>

            <p className="mt-2 text-sm font-medium text-white/65">
              Question {currentQuestionIndex + 1} / {questions.length}
            </p>
          </div>

          <button
            type="button"
            onClick={generateQuiz}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-slate-950 transition hover:-translate-y-0.5"
          >
            <FaRedo />
            Nouveau quiz
          </button>
        </div>

        <div className="mt-5 h-3 overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full rounded-full bg-sky-300 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {currentQuestion && (
        <div className="grid gap-5 p-4 sm:p-6 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="order-2 grid content-center gap-3 lg:order-1">
            {currentQuestion.options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleAnswer(option)}
                className="inline-flex min-h-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-5 py-3 text-base font-bold text-slate-800 transition hover:-translate-y-0.5 hover:border-sky-200 hover:bg-white hover:text-sky-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-sky-300/40 dark:hover:text-sky-100"
              >
                {option}
              </button>
            ))}
          </div>

          <div className="order-1 overflow-hidden rounded-[1.5rem] bg-slate-100 dark:bg-slate-950 lg:order-2">
            <img
              src={currentQuestion.image || DEFAULT_TECHNIQUE_IMAGE}
              alt="Technique à reconnaître"
              className="h-[420px] w-full object-cover"
            />
          </div>
        </div>
      )}
    </section>
  );
}

export default TechniqueQuizPanel;